import { Fragment } from 'react';

import type { ReactNode } from 'react';

/**
 * Isolate URL paths inside right-to-left prose.
 *
 * `/pricing` written in a Hebrew sentence renders as `pricing/`. The leading
 * slash is a bidi-NEUTRAL character, so when it sits between Hebrew text and a
 * Latin run the Unicode bidi algorithm resolves it to the paragraph direction
 * and lays it out on the far side of the word. The path is then wrong in the one
 * way that matters: a reader cannot tell what to type into the address bar.
 *
 * Measured on the shipped catalogs rather than reasoned about, because the
 * resolution depends on the character immediately BEFORE the path: **15 Hebrew
 * strings and 4 Arabic ones** rendered the slash on the wrong side, and the two
 * locales disagreed about which — `docs.dashboard.audit.lead` was broken in
 * Hebrew and correct in Arabic, from the same English source. Any rule derived
 * by reading the strings would have got that wrong in both directions.
 *
 * `<bdi>` is the fix rather than an LRM or an `&#x2066;` isolate pair: it is
 * declarative, survives a translator round-trip (an invisible control character
 * does not), and its `dir="auto"` default resolves from the first strong
 * character in the run — which for `/orders` is the `o`, so the path becomes its
 * own left-to-right island regardless of the paragraph around it.
 *
 * It is a no-op in LTR locales, where the neutral slash already resolves the way
 * the author meant.
 */

/**
 * A path in prose: a leading `/`, then a lowercase segment, then any number of
 * further segments — including a `{param}` placeholder, which the docs use for
 * route shapes like `/orders/{id}/refund`.
 *
 * Deliberately NOT a general URL matcher. A bare `https://…` starts with a
 * strong LTR character (`h`), so the bidi algorithm already gets it right and
 * wrapping it would be noise. The bug is specific to a leading neutral.
 */
const PROSE_PATH = /((?:^|(?<=[^\w/]))\/[a-z][a-z0-9-]{2,}(?:\/[A-Za-z0-9{}:_-]+)*)/g;

/**
 * Split `text` on any URL path and wrap each one in `<bdi>`.
 *
 * Returns the string unchanged when it holds no path, so the common case adds no
 * elements to the tree.
 */
export function isolatePaths(text: string): ReactNode {
  if (!text.includes('/')) return text;
  const parts = text.split(PROSE_PATH);
  if (parts.length === 1) return text;
  // `String.split` with ONE capture group interleaves: even indices are the
  // surrounding text, odd indices are the captured paths. Using parity rather
  // than re-testing each part matters — `PROSE_PATH` carries the `g` flag, so
  // `.test()` advances `lastIndex` and would return alternating answers for the
  // same input.
  return parts.map((part, i) =>
    i % 2 === 1 ? <bdi key={i}>{part}</bdi> : <Fragment key={i}>{part}</Fragment>,
  );
}
